/**
 * MAPCLUB SSO — login and register page enhancements.
 *
 * Both pages render most of their form from the mapclub-sso app block, which
 * we cannot edit. Where the design needs a node somewhere the app does not put
 * it, we move or clone the app's own node rather than re-authoring its text —
 * that keeps the wording, the links and the translations owned by the app.
 *
 * Login — the consent copy ("By signing up, you agree to receive any communications...")
 * is rendered by the mapclub-sso app block inside #login-popup. The login page
 * design needs that same copy under the Login button, so we clone the node
 * instead of re-authoring the text here — that keeps the wording, the policy
 * links and the translation owned by the app.
 *
 * Once the clone is mounted we flag the document so mapclub.css can hide the
 * original inside the popup. Doing it from JS (rather than a plain CSS rule)
 * means the copy is never hidden unless a replacement actually made it onto
 * the page.
 */
(function () {
  'use strict';

  /**
   * Per-brand configuration. This file is shared across the ALDO, Kidzstation
   * and LEGO themes; this block is the only part that differs between the
   * copies, so a brand is onboarded by editing it rather than the logic below.
   *
   * lockup/banner: the paired "brand + MAPCLUB" artwork. null leaves whatever
   * the app renders, which is what a brand without its own artwork wants —
   * pointing at a missing asset would only produce a broken image.
   *
   * accountSidebar: where the account page's MAPCLUB block belongs. null on
   * themes with no sidebar to move it into, which also skips the hide-while-
   * relocating step, so the block is never hidden waiting for a move that
   * cannot happen.
   */
  var CONFIG = {
    // Brand lockup built from this theme's own header logo. No link banner
    // yet, so the app's own artwork still stands for that one.
    lockup: 'skechers-mapclub-logo.png',
    banner: null,
    wordmark: 'mapclub-logo.png',
    // This theme uses the stock account section, which has no sidebar to move
    // the MAPCLUB block into, so it stays where the app puts it.
    accountSidebar: null
  };

  var SOURCE = '#login-popup #auth > h4';
  var ANCHOR = '.login_new_design .left_login_form form#customer_login';
  var MOUNTED_CLASS = 'mapclub-consent-mounted';
  var TIMEOUT_MS = 10000;
  var ACCOUNT_PENDING = 'mapclub-account-pending';
  var MOVE_POLL_MS = 100;
  // Generous: this preview has taken 20s+ to render the sidebar, and the app
  // injects the block just after it. Overrunning is cheap — the app keeps the
  // block hidden itself until its points call resolves, and the pending class
  // does nothing at all while the block is absent.
  var MOVE_TIMEOUT_MS = 30000;
  var accountRelocating = false;
  var loginPopupWatched = false;

  /**
   * Resolve a theme asset URL without Liquid. This file already sits in the
   * theme's asset directory, so its own <link> gives us the versioned base to
   * hang other filenames off.
   */
  function themeAssetUrl(name) {
    var link = document.querySelector('link[href*="/assets/mapclub.css"]');
    if (!link) return null;
    return link.href.split('/assets/')[0] + '/assets/' + name;
  }

  /**
   * Point an app-rendered <img> at one of our theme assets. The app hardcodes
   * its own src, and the sizing attributes it ships describe the old artwork,
   * so drop them and let the stylesheet size the replacement.
   */
  function swapLogo(img, name) {
    // name is null when the brand has no artwork of its own — leave the app's.
    if (!img || !name) return;
    var url = themeAssetUrl(name);
    if (!url || img.getAttribute('src') === url) return;
    img.setAttribute('src', url);
    // These describe the old artwork — the new asset has a different aspect
    // ratio, so let the stylesheet size it. Every selector below has an
    // explicit width; without one the image falls back to its natural size.
    img.removeAttribute('width');
    img.removeAttribute('height');
    img.removeAttribute('style');
  }

  /**
   * The same MAPCLUB wordmark appears in every consent block: inside the login
   * popup, in the clone the login page mounts from it, and on the register
   * page's own form.
   */
  var CONSENT_LOGOS = [
    '#login-popup #auth > h4 img',
    '.mapclub-login-consent img',
    '#signup > h4 img',
    '#registration > h4 img'
  ].join(', ');

  function swapConsentLogos() {
    Array.prototype.forEach.call(document.querySelectorAll(CONSENT_LOGOS), function (img) {
      swapLogo(img, CONFIG.wordmark);
    });
  }

  function mount() {
    if (document.documentElement.classList.contains(MOUNTED_CLASS)) return true;

    var source = document.querySelector(SOURCE);
    var anchor = document.querySelector(ANCHOR);
    if (!source || !anchor) return false;

    var consent = source.cloneNode(true);
    consent.removeAttribute('id');
    consent.className = 'mapclub-login-consent';

    // The popup hard-wraps this copy to fit its narrow column. The page column
    // is wider, so swap the breaks ahead of the MAPCLUB logo for spaces and let
    // the text reflow; the break before the policy links is kept. They must
    // become spaces, not vanish, or the words either side run together.
    var logo = consent.querySelector('img');
    if (logo) {
      Array.prototype.forEach.call(consent.querySelectorAll('br'), function (br) {
        if (br.compareDocumentPosition(logo) & Node.DOCUMENT_POSITION_FOLLOWING) {
          br.parentNode.replaceChild(document.createTextNode(' '), br);
        }
      });
    }

    anchor.insertAdjacentElement('afterend', consent);
    document.documentElement.classList.add(MOUNTED_CLASS);
    return true;
  }

  /**
   * Register page: the app renders the phone label inside the field as a
   * floating label. The design puts it above the control, like the Email* and
   * Password* labels on login. Move the existing node rather than writing our
   * own, so the app keeps ownership of the wording and its translation.
   */
  /**
   * The SSO button reads "Sign In with MAPCLUB", all of it text. The design
   * wants the brand set as the wordmark instead, so swap that one word for
   * the image and leave the rest of the label alone. The app's own bag mark
   * before it is hidden in the stylesheet.
   */
  var SIGNIN_WORDMARK = 'mapclub-signin-wordmark';

  function applyMapclubButton() {
    var button = document.querySelector('#signin > button.button--secondary');
    if (!button) return false;
    if (button.querySelector('.' + SIGNIN_WORDMARK)) return true;

    var url = themeAssetUrl(CONFIG.wordmark);
    if (!url) return true; // no asset base to build from; leave the text as it is

    var target = null;
    Array.prototype.forEach.call(button.childNodes, function (node) {
      if (!target && node.nodeType === 3 && /MAPCLUB/i.test(node.nodeValue)) target = node;
    });
    if (!target) return false;

    var match = /MAPCLUB/i.exec(target.nodeValue);
    var image = document.createElement('img');
    image.className = SIGNIN_WORDMARK;
    image.src = url;
    image.alt = target.nodeValue.substr(match.index, match[0].length);

    var parent = target.parentNode;
    parent.insertBefore(document.createTextNode(target.nodeValue.slice(0, match.index)), target);
    parent.insertBefore(image, target);
    parent.insertBefore(document.createTextNode(target.nodeValue.slice(match.index + match[0].length)), target);
    parent.removeChild(target);
    return true;
  }

  function liftPhoneLabel() {
    var wrapper = document.querySelector('#signup .phone-field-wrapper');
    if (!wrapper) return false;
    if (wrapper.parentNode.querySelector('.mapclub-phone-label')) return true;

    var label = wrapper.querySelector('.phone-input label');
    if (!label) return false;

    label.classList.add('mapclub-phone-label');
    wrapper.parentNode.insertBefore(label, wrapper);
    return true;
  }

  /**
   * Register page copy overrides.
   *
   * These strings are hardcoded in the mapclub-sso extension — its app embed
   * exposes no text settings — and the design calls for different wording.
   * Applied on the English storefront only: other locales keep the app's own
   * translated copy rather than being forced into English. If these need to
   * change for good, the durable fix is in the app, not here.
   */
  var EN_REGISTER_COPY = [
    {
      sel: '#signup > h3',
      text: 'Create an account and enjoy faster checkout, save multiple addresses, track your orders, and more!'
    },
    { sel: '#signup button.submit', text: 'Register' },
    { sel: '#signup .mapclub-phone-label, #signup .phone-input label', text: 'Phone Number*' }
  ];
  var EN_PHONE_PLACEHOLDER = 'Enter your phone number';

  function isEnglish() {
    return (window.Shopify && Shopify.locale ? Shopify.locale : 'en').split('-')[0] === 'en';
  }

  function applyRegisterCopy() {
    if (!isEnglish()) return;

    EN_REGISTER_COPY.forEach(function (rule) {
      var el = document.querySelector(rule.sel);
      // Guard the compare so writing back does not re-trigger the observer.
      if (el && el.textContent.trim() !== rule.text) el.textContent = rule.text;
    });

    var input = document.querySelector('#signup .phone-input input');
    if (input && input.getAttribute('placeholder') !== EN_PHONE_PLACEHOLDER) {
      input.setAttribute('placeholder', EN_PHONE_PLACEHOLDER);
    }
  }

  /**
   * Register page, email step (#registration) — the counterpart to the phone
   * step above, shown when the flow asks for an email instead. Same treatment:
   * the label lifted above the field, and the design's wording on English.
   *
   * Its h3 is left alone: unlike the phone step's, it is a specific
   * instruction ("enter your email address to proceed"), not marketing copy.
   */
  function liftRegistrationLabel() {
    var field = document.querySelector('#registration > .field');
    if (!field || field.parentNode.querySelector('.mapclub-registration-label')) return;

    var label = field.querySelector('label');
    if (!label) return;

    label.classList.add('mapclub-registration-label');
    field.parentNode.insertBefore(label, field);
  }

  var EN_REGISTRATION_COPY = [
    { sel: '#registration .mapclub-registration-label, #registration .field label', text: 'Email*' },
    { sel: '#registration button.submit', text: 'Register' }
  ];

  function applyRegistrationStep() {
    if (!document.querySelector('#registration')) return;
    liftRegistrationLabel();
    if (!isEnglish()) return;

    EN_REGISTRATION_COPY.forEach(function (rule) {
      var el = document.querySelector(rule.sel);
      if (el && el.textContent.trim() !== rule.text) el.textContent = rule.text;
    });
  }

  /**
   * Register page, full account form: the design shows the required-field
   * asterisk in red, but it arrives inside translated strings. Wrap just the
   * trailing character so CSS can colour it, leaving the wording untouched.
   */
  function markRequiredAsterisks() {
    var form = document.querySelector('#create_customer');
    if (!form) return;

    var labels = form.querySelectorAll('.field__label, .gender_register_heading, .birthday_heading');
    Array.prototype.forEach.call(labels, function (label) {
      if (label.querySelector('.mapclub-req')) return;

      var last = label.lastChild;
      while (last && last.nodeType !== 3) last = last.previousSibling;
      if (!last || last.nodeValue.replace(/\s+$/, '').slice(-1) !== '*') return;

      var text = last.nodeValue.replace(/\s+$/, '');
      last.nodeValue = text.slice(0, -1);

      var star = document.createElement('span');
      star.className = 'mapclub-req';
      star.textContent = '*';
      last.parentNode.insertBefore(star, last.nextSibling);
    });
  }

  /**
   * Sign-up dialog (#register-popup) — a full form, so every field's floating
   * label is lifted above its control the same way. The consent copy lives
   * inside a checkbox label here rather than in an h4, so it is not part of
   * CONSENT_LOGOS and its wordmark is swapped explicitly.
   */
  function applyRegisterPopup() {
    var popup = document.querySelector('#register-popup');
    if (!popup) return;

    swapLogo(popup.querySelector('.customer > img'), CONFIG.lockup);
    swapLogo(popup.querySelector('#register .checkbox label img'), CONFIG.wordmark);

    Array.prototype.forEach.call(popup.querySelectorAll('#register > .field'), function (field) {
      if (field.previousElementSibling &&
        field.previousElementSibling.classList.contains('mapclub-popup-label')) return;

      var label = field.querySelector('label');

      // The gender field ships a <select> with no label of its own. Build one
      // from the placeholder the app already put on it, rather than authoring
      // a string here that no locale would translate.
      if (!label) {
        var select = field.querySelector('select[placeholder]');
        if (!select) return;

        label = document.createElement('label');
        label.textContent = select.getAttribute('placeholder');
        if (!select.id) select.id = 'mapclub-register-gender';
        label.setAttribute('for', select.id);
      }

      label.classList.add('mapclub-popup-label');
      field.parentNode.insertBefore(label, field);
    });
  }

  /**
   * "Choose Verification Method" dialog.
   *
   * Same story as the register copy: the app hardcodes the headings, so the
   * English storefront gets the design's wording and other locales keep the
   * app's own. The phone number is different — the register field has a fixed
   * +62 prefix that the app drops when it echoes the number back, so we put it
   * back rather than show a number the customer cannot recognise.
   */
  var VERIFY_TITLE = 'Choose Verification Method';
  var VERIFY_SUBTITLE = 'Select a verification method to send the verification code to:';
  var verificationWatched = false;

  function applyVerificationCopy() {
    var popup = document.querySelector('#verification-popup');
    if (!popup) return;

    if (isEnglish()) {
      var h2 = popup.querySelector('h2');
      if (h2 && h2.textContent.trim() !== VERIFY_TITLE) h2.textContent = VERIFY_TITLE;
      var h4 = popup.querySelector('h4');
      if (h4 && h4.textContent.trim() !== VERIFY_SUBTITLE) h4.textContent = VERIFY_SUBTITLE;
    }

    var phone = popup.querySelector('#phone');
    if (phone) {
      var raw = phone.textContent.trim();
      // Guard on the leading + so re-entry never stacks prefixes.
      if (raw && raw.charAt(0) !== '+') {
        phone.textContent = '+62' + raw.replace(/^0+/, '');
      }
    }
  }

  /**
   * "OTP Verification" dialog.
   */
  var OTP_COPY = {
    validity: 'OTP code valid for 5 minutes',
    label: 'Enter OTP Code',
    noCode: 'Doesn’t receive code?',
    resend: 'Resend code in '
  };

  function applyOtpCopy() {
    var popup = document.querySelector('#otp-popup');
    if (!popup || !isEnglish()) return;

    var content = popup.querySelector('.signup-popup-content');
    if (!content) return;

    var h3 = content.querySelectorAll('h3');
    if (h3[0] && h3[0].textContent.trim() !== OTP_COPY.validity) h3[0].textContent = OTP_COPY.validity;
    if (h3[1] && h3[1].textContent.trim() !== OTP_COPY.label) h3[1].textContent = OTP_COPY.label;

    var h4 = content.querySelector('h4');
    if (h4 && h4.textContent.trim() !== OTP_COPY.noCode) h4.textContent = OTP_COPY.noCode;

    var h5 = content.querySelector('h5');
    var timer = h5 && h5.querySelector('#otpTimer');
    // Only rewrite while the countdown is running. Replace the surrounding text
    // node rather than h5.textContent, which would delete the span the app
    // ticks every second. Once it expires the app swaps in its own link, and
    // this leaves that alone.
    if (timer) {
      var done = false;
      Array.prototype.slice.call(h5.childNodes).forEach(function (node) {
        if (node.nodeType !== 3) return;
        if (!done) {
          if (node.nodeValue !== OTP_COPY.resend) node.nodeValue = OTP_COPY.resend;
          done = true;
        } else if (node.nodeValue.trim()) {
          node.nodeValue = '';
        }
      });
    }
  }

  /**
   * "Account Already Registered" dialog.
   *
   * #confirm-dialog is a generic dialog the app reuses, so every change here is
   * gated on its message actually being the already-registered one — otherwise
   * an unrelated confirmation would inherit this heading and button label.
   *
   * The app renders one sentence where the design has a heading and a
   * subtitle, so the heading is added; the design's "Your MAPCLUB Account"
   * card is deliberately not, as the API does not return that name.
   */
  var ALREADY_REGISTERED = /already registered/i;
  var CONFIRM_COPY = {
    title: 'Account Already Registered',
    body: 'This account is already registered. Please link to continue.',
    primary: 'Link'
  };

  function applyConfirmDialog() {
    var dialog = document.querySelector('#confirm-dialog');
    var content = dialog && dialog.querySelector('.modal-dialog-content');
    if (!content) return;

    // Every message this dialog carries gets the lockup, so swap it before
    // the copy-specific work below, which returns early for anything but the
    // "already registered" case and used to take the logo with it.
    swapLogo(content.querySelector('img'), CONFIG.lockup);

    var body = content.querySelector('h3');
    var title = content.querySelector('.mapclub-dialog-title');
    if (!body) return;
    if (!title && !ALREADY_REGISTERED.test(body.textContent)) return;
    if (!isEnglish()) return;

    if (!title) {
      title = document.createElement('h2');
      title.className = 'mapclub-dialog-title';
      title.textContent = CONFIRM_COPY.title;
      content.insertBefore(title, body);
    }
    if (body.textContent.trim() !== CONFIRM_COPY.body) body.textContent = CONFIRM_COPY.body;

    var primary = content.querySelector('.button--primary');
    if (primary && primary.textContent.trim() !== CONFIRM_COPY.primary) {
      primary.textContent = CONFIRM_COPY.primary;
    }
  }

  /**
   * The sign-in failure this dialog carries, in the brand's own wording.
   *
   * Unlike the field messages above, the app writes this one in English on
   * every storefront, so it cannot be gated on the locale: match it on its
   * own text and supply both. Once replaced the pattern no longer matches,
   * which is what keeps the observer from rewriting it on every pass.
   */
  var MODAL_BAD_CREDENTIALS = /check again your mobile number or email/i;
  var MODAL_BAD_CREDENTIALS_COPY = {
    en: 'Login failed, Invalid phone number or password',
    id: 'Login gagal, nomor telepon atau kata sandi tidak valid'
  };

  /**
   * #modal-dialog is the app's plain message dialog (bad credentials, and
   * similar). It gets the same chrome as the other dialogs, and the one
   * message the brand has its own wording for; anything else the app puts
   * here is left alone.
   */
  function applyModalDialog() {
    var content = document.querySelector('#modal-dialog .modal-dialog-content');
    if (!content) return;
    swapLogo(content.querySelector('img'), CONFIG.lockup);

    var heading = content.querySelector('h3');
    if (!heading) return;
    var text = heading.textContent.trim();
    if (!MODAL_BAD_CREDENTIALS.test(text)) return;
    var want = isEnglish() ? MODAL_BAD_CREDENTIALS_COPY.en : MODAL_BAD_CREDENTIALS_COPY.id;
    if (text !== want) heading.textContent = want;
  }

  /**
   * Link-account dialog (#option-dialog) and the MAPCLUB sign-up dialog.
   *
   * #option-dialog is another generic shell the app fills at runtime, so the
   * banner is only swapped in when its copy is actually the link prompt —
   * otherwise an unrelated prompt would inherit the artwork.
   */
  function applyOptionDialog() {
    var content = document.querySelector('#option-dialog .modal-dialog-content');
    if (content) {
      var heading = content.querySelector('h3');
      // The link prompt gets its own designed banner where the brand has one.
      // Everything else here - and every brand without that artwork - takes the
      // same lockup as the other two dialogs rather than the app's own logo.
      var img = content.querySelector('img');
      var banner = heading && /link/i.test(heading.textContent) ? CONFIG.banner : null;
      swapLogo(img, banner || CONFIG.lockup);
      // The banner is full-bleed; the lockup is inset like the other dialogs.
      if (img) img.classList.toggle('mapclub-dialog-banner', !!banner);
    }

    swapLogo(document.querySelector('#signup-popup .customer > img'), CONFIG.lockup);
  }

  function applyPopupCopy() {
    applyVerificationCopy();
    applyOtpCopy();
    applyConfirmDialog();
    applyModalDialog();
    applyOptionDialog();
    applyRegistrationStep();
    applyRegisterPopup();
    markRequiredAsterisks();
    // #signup is the register page's phone step, but it is also what
    // #signup-popup fills itself with, so lift its label wherever it appears.
    liftPhoneLabel();
    swapConsentLogos();
  }

  function watchVerificationPopup() {
    if (verificationWatched) return true;

    // #signup is the register page's own form, watched for the same reason:
    // the app re-renders it as the flow advances.
    var popups = ['#verification-popup', '#otp-popup', '#code-popup', '#signup', '#registration',
      '#register-popup', '#signup-popup', '#create_customer', '#confirm-dialog',
      '#modal-dialog', '#option-dialog']
      .map(function (sel) { return document.querySelector(sel); })
      .filter(Boolean);
    if (!popups.length) return false;

    applyPopupCopy();
    // The dialogs are populated and toggled via style, so watch both. Writes
    // are value-guarded above, so re-entry settles instead of looping — this
    // observer fires once a second while the OTP countdown ticks.
    popups.forEach(function (popup) {
      new MutationObserver(applyPopupCopy).observe(popup, {
        childList: true,
        subtree: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['style']
      });
    });
    verificationWatched = true;
    return true;
  }

  /**
   * Account page: the app injects its MAPCLUB summary as a full-width banner
   * above the order history. The design places it in the sidebar, between the
   * account details and address cards. The two live under different parents,
   * so CSS cannot reparent it — move the node the app rendered.
   */
  /**
   * Keyed on the class, not the id: the app renders #mapclub for members and
   * #non-mapclub for everyone else — same block, different id — and both
   * belong in the sidebar. Whichever is present is moved; the app decides
   * which one it shows.
   */
  function moveMapclubBlock() {
    var blocks = document.querySelectorAll('.mapclub-account');
    var sidebar = CONFIG.accountSidebar && document.querySelector(CONFIG.accountSidebar);
    if (!blocks.length || !sidebar) return false;

    var address = sidebar.querySelector('.primary-address-block');
    if (!address) return false;

    Array.prototype.forEach.call(blocks, function (block) {
      if (block.parentElement !== sidebar) sidebar.insertBefore(block, address);
    });
    return true;
  }

  /**
   * The design reads "MAPCLUB Account", where MAPCLUB is the wordmark image.
   * The app renders the image alone, so the word is added beside it.
   */
  function ensureAccountWord(block) {
    var img = block && block.querySelector('img');
    if (!img || block.querySelector('.mapclub-account-word')) return;

    var word = document.createElement('span');
    word.className = 'mapclub-account-word';
    word.textContent = 'Account';
    img.insertAdjacentElement('afterend', word);
  }

  function applyAccountBlock() {
    var moved = moveMapclubBlock();
    Array.prototype.forEach.call(document.querySelectorAll('.mapclub-account'), function (block) {
      swapLogo(block.querySelector('img'), CONFIG.wordmark);
      ensureAccountWord(block);
    });
    // The link-account prompt is raised on this page too, and the app fills it
    // well after load — the observer this runs from is what catches it.
    applyOptionDialog();
    return moved;
  }

  /**
   * Hide the block, then poll until it can be moved, revealing it in its new
   * home. Polling rather than observing mutations because the block can
   * already be in the DOM before the sidebar is ready, in which case no
   * further mutation would arrive to react to.
   *
   * The reveal is in a finally-style path: whether the move lands or the
   * deadline passes, the pending class comes off, so the block can never be
   * left permanently hidden.
   */
  function relocateMapclubBlock() {
    if (accountRelocating) return;
    accountRelocating = true;

    // One move is not enough: the app re-parents this block itself once its
    // profile call resolves — observed moving it out of its app-block
    // container and into the page body about six seconds in, which silently
    // undid the relocation. Keep watching and put it back every time.
    // moveMapclubBlock is a no-op once the block is already in the sidebar, so
    // reacting to our own move settles instead of looping. It also carries the
    // logo and wording for themes that never move the block at all.
    new MutationObserver(function () {
      applyAccountBlock();
    }).observe(document.body, { childList: true, subtree: true });

    // A theme with no sidebar to move into has nothing to relocate, so there
    // is no in-between position to hide. Hiding it regardless left the block
    // invisible until the timeout below gave up on a move that could never
    // succeed - a full thirty seconds after the profile call had returned.
    if (!CONFIG.accountSidebar) {
      applyAccountBlock();
      return;
    }

    var root = document.documentElement;
    var reveal = function () { root.classList.remove(ACCOUNT_PENDING); };

    root.classList.add(ACCOUNT_PENDING);
    if (applyAccountBlock()) {
      reveal();
      return;
    }

    var deadline = Date.now() + MOVE_TIMEOUT_MS;
    var timer = setInterval(function () {
      if (applyAccountBlock() || Date.now() > deadline) {
        clearInterval(timer);
        reveal();
      }
    }, MOVE_POLL_MS);
  }

  /**
   * Login popup (#login-popup) — the design's paired ALDO/MAPCLUB lockup,
   * labels above their fields rather than floating inside them, and the
   * consent copy left in place.
   */
  var LOGIN_POPUP_COPY = {
    title: 'Login',
    // On the account page this popup is not a sign-in: the customer is already
    // signed in and is connecting an existing MAPCLUB account to it.
    linkTitle: 'Link to MAPCLUB',
    submit: 'Login',
    phone: 'Phone Number*',
    password: 'Password*',
    // Wording this brand asked for on the failed sign-in messages. The app
    // translates both, so these only stand in for the English.
    phoneError: 'Phone number not register / invalid please recheck again.',
    passwordError: 'Password invalid please re-input again.'
  };

  function liftPopupLabel(field, before, marker) {
    if (!field || !before) return null;
    var label = field.querySelector('label');
    if (!label) return before.parentNode.querySelector('.' + marker);
    if (label.classList.contains('mapclub-popup-label')) return label;

    label.classList.add('mapclub-popup-label', marker);
    before.parentNode.insertBefore(label, before);
    return label;
  }

  /**
   * "Password is required" is in the popup's markup with its text already
   * written, but without the is-hidden its three sibling messages carry, so
   * it is on screen the moment the popup opens. The app never touches it
   * again - not on input, not on blur, not on submit - so the state it meant
   * to ship has to come from here: hidden until a submit finds the field
   * empty, and hidden again as soon as something is typed.
   *
   * It uses the app's own class, so if the app ever starts managing this
   * message the two agree rather than fight.
   */
  var HIDDEN_CLASS = 'is-hidden';
  var PASSWORD_GUARD = 'data-mapclub-password-guard';

  function guardPasswordMessage(popup) {
    var message = popup.querySelector('.form__message.first-step-password');
    var field = popup.querySelector('#auth input[type="password"]');
    if (!message || !field) return;
    if (message.getAttribute(PASSWORD_GUARD)) return;
    message.setAttribute(PASSWORD_GUARD, '1');

    var hide = function () { message.classList.add(HIDDEN_CLASS); };
    hide();
    field.addEventListener('input', hide);

    // Capture: settle this before the app's own submit handler runs.
    popup.addEventListener('click', function (event) {
      var target = event.target;
      if (!target || !target.closest) return;
      if (!target.closest('#auth .submit, #auth button[type="submit"]')) return;
      if (field.value.trim()) hide();
      else message.classList.remove(HIDDEN_CLASS);
    }, true);
  }

  function applyLoginPopup() {
    var popup = document.querySelector('#login-popup');
    if (!popup) return false;

    swapLogo(popup.querySelector('.signin-popup-content > .customer > img'), CONFIG.lockup);
    swapConsentLogos();

    var phoneWrap = popup.querySelector('#auth .phone-field-wrapper');
    var phoneLabel = phoneWrap
      ? liftPopupLabel(phoneWrap.querySelector('.phone-input .field'), phoneWrap, 'mapclub-label-phone')
      : null;

    // This app build wraps the password in .password-field-wrapper, together
    // with its own show/hide button; older ones put the field straight under
    // #auth. Looking only for the latter left the label unlifted here, so the
    // field stayed a flex row and the label sat beside the input.
    var pwWrap = popup.querySelector('#auth .password-field-wrapper');
    var pwField = pwWrap ? pwWrap.querySelector('.field') : popup.querySelector('#auth > .field');
    var pwLabel = pwField ? liftPopupLabel(pwField, pwWrap || pwField, 'mapclub-label-password') : null;

    guardPasswordMessage(popup);

    if (isEnglish()) {
      var set = function (el, text) {
        if (el && el.textContent.trim() !== text) el.textContent = text;
      };
      var onAccount = document.body.classList.contains('template_account');
      set(popup.querySelector('.customer > h2'),
        onAccount ? LOGIN_POPUP_COPY.linkTitle : LOGIN_POPUP_COPY.title);
      set(popup.querySelector('#auth button.submit'), LOGIN_POPUP_COPY.submit);
      set(phoneLabel, LOGIN_POPUP_COPY.phone);
      set(pwLabel, LOGIN_POPUP_COPY.password);
      set(popup.querySelector('.form__message.second-step-phone'), LOGIN_POPUP_COPY.phoneError);
      set(popup.querySelector('.form__message.second-step-password'), LOGIN_POPUP_COPY.passwordError);
    }

    return true;
  }

  function watchLoginPopup() {
    if (loginPopupWatched) return true;
    var popup = document.querySelector('#login-popup');
    if (!popup) return false;

    var apply = function () {
      applyLoginPopup();
      applyModalDialog();
    };
    apply();

    // The app re-renders these as the sign-in flow advances, which would drop
    // the adjustments. Writes are value-guarded, so reacting to our own
    // changes settles instead of looping.
    [popup, document.querySelector('#modal-dialog')].filter(Boolean).forEach(function (el) {
      new MutationObserver(apply).observe(el, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['style', 'class', 'src']
      });
    });
    loginPopupWatched = true;
    return true;
  }

  /**
   * The app injects its popups and dialogs on every template, not only the
   * page each belongs to — the account page raises the sign-in and sign-up
   * popups and the link prompt. So the popup work is template-agnostic, and
   * only genuinely page-specific work is gated below.
   */
  function run() {
    var body = document.body;
    var ready = true;

    if (!watchLoginPopup()) ready = false;
    if (!watchVerificationPopup()) ready = false;

    if (body.classList.contains('template_login')) {
      if (!mount()) ready = false;
      if (!applyMapclubButton()) ready = false;
    }
    if (body.classList.contains('template_register')) {
      // Page-specific: the design's marketing subtitle belongs on the register
      // page, not in the popup, where the app's own instruction is apter.
      applyRegisterCopy();
    }
    if (body.classList.contains('template_account')) {
      relocateMapclubBlock();
    }
    return ready;
  }

  /**
   * No template gate here. theme.liquid already loads this file on the four
   * templates it belongs to, and every step below guards its own targets — so
   * gating again only risked missing a page. It did: the link prompt is raised
   * on the home page too, where a gate on login/register/account meant the
   * stylesheet applied while none of this ran.
   */
  function init() {
    if (run()) return;

    // The app block renders these asynchronously, so they may not be in the
    // DOM yet on DOMContentLoaded.
    var observer = new MutationObserver(function () {
      if (run()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(function () {
      observer.disconnect();
    }, TIMEOUT_MS);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
